/**
 * IntegrationsSettings — Pro "bring your own API" connections (0031).
 *
 * Image generation (fal.ai, Kie.ai) works end to end today: connect a key,
 * test-generate here, then attach images to LinkedIn posts from the Content
 * composer. Cold email (Instantly, Smartlead) and GoHighLevel store and
 * validate the connection now; the automations that use them ship next.
 *
 * Keys are encrypted server side and only the last 4 characters ever come
 * back to the browser.
 */

import { useState } from 'react'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import {
  Loader2,
  Plug,
  ImageIcon,
  Mail,
  Building2,
  CheckCircle2,
  Sparkles,
} from 'lucide-react'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { Badge } from '@/components/ui/badge'
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from '@/components/ui/card'
import { PlanGate } from '@/components/plan-gate'
import { useApi } from '@/hooks/use-api'
import { usePlan } from '@/hooks/use-plan'

const integrationKeys = { list: ['settings', 'integrations'] as const }

export interface IntegrationRow {
  provider: string
  label: string
  category: 'image' | 'cold_email' | 'crm'
  config_fields: string[]
  capability: string
  connected: boolean
  key_last4: string | null
  status: string | null
  last_validated_at: string | null
  config: Record<string, string>
}

const CATEGORY_META: Record<
  IntegrationRow['category'],
  { title: string; desc: string; Icon: React.ComponentType<{ className?: string }> }
> = {
  image: {
    title: 'Image Generation',
    desc: 'Generate images with your own API key and attach them to LinkedIn posts from the Content composer.',
    Icon: ImageIcon,
  },
  cold_email: {
    title: 'Cold Email',
    desc: 'Connect your cold email platform. Pushing DealCloser leads into your campaigns is coming soon.',
    Icon: Mail,
  },
  crm: {
    title: 'CRM',
    desc: 'Connect GoHighLevel with a Private Integration token. Syncing replied leads into your CRM is coming soon.',
    Icon: Building2,
  },
}

const CONFIG_FIELD_LABELS: Record<string, string> = {
  location_id: 'Location ID',
}

function ProviderCard({ row }: { readonly row: IntegrationRow }) {
  const queryClient = useQueryClient()
  const apiFetch = useApi()
  const [apiKey, setApiKey] = useState('')
  const [config, setConfig] = useState<Record<string, string>>(row.config ?? {})
  const [note, setNote] = useState<string | null>(null)

  const connectMutation = useMutation({
    mutationFn: async (): Promise<string | undefined> => {
      const res = await apiFetch<{ validated?: boolean; note?: string }>(
        `/api/settings/integrations/${row.provider}`,
        { method: 'PUT', body: { api_key: apiKey, config } },
      )
      if (!res.success) throw new Error(res.error ?? 'Failed to connect')
      return res.data?.note
    },
    onSuccess: (serverNote) => {
      setApiKey('')
      setNote(serverNote ?? null)
      void queryClient.invalidateQueries({ queryKey: integrationKeys.list })
    },
  })

  const disconnectMutation = useMutation({
    mutationFn: async (): Promise<void> => {
      const res = await apiFetch(`/api/settings/integrations/${row.provider}`, {
        method: 'DELETE',
      })
      if (!res.success) throw new Error(res.error ?? 'Failed to disconnect')
    },
    onSuccess: () => {
      setNote(null)
      void queryClient.invalidateQueries({ queryKey: integrationKeys.list })
    },
  })

  const busy = connectMutation.isPending || disconnectMutation.isPending
  const error =
    (connectMutation.error instanceof Error ? connectMutation.error.message : null) ??
    (disconnectMutation.error instanceof Error ? disconnectMutation.error.message : null)

  return (
    <div className="rounded-lg border p-4">
      <div className="flex flex-wrap items-center justify-between gap-2">
        <div className="flex items-center gap-2">
          <span className="text-sm font-semibold">{row.label}</span>
          {row.connected ? (
            <Badge variant="secondary" className="gap-1 text-[10px]">
              <CheckCircle2 className="h-3 w-3 text-emerald-400" />
              Connected {row.key_last4 ? `(****${row.key_last4})` : ''}
            </Badge>
          ) : (
            <Badge variant="outline" className="text-[10px]">Not connected</Badge>
          )}
        </div>
        {row.connected && (
          <Button
            variant="ghost"
            size="sm"
            className="h-7 text-xs text-destructive hover:text-destructive"
            onClick={() => disconnectMutation.mutate()}
            disabled={busy}
          >
            Disconnect
          </Button>
        )}
      </div>
      <p className="mt-1 text-xs text-muted-foreground">{row.capability}</p>

      <div className="mt-3 flex flex-col gap-2 sm:flex-row sm:items-end">
        <div className="flex-1 space-y-1">
          <Label htmlFor={`key-${row.provider}`} className="text-xs">
            {row.connected ? 'Replace API key' : 'API key'}
          </Label>
          <Input
            id={`key-${row.provider}`}
            type="password"
            autoComplete="off"
            placeholder={row.connected ? 'Paste a new key to replace the stored one' : 'Paste your API key'}
            value={apiKey}
            onChange={(e) => setApiKey(e.target.value)}
            disabled={busy}
          />
        </div>
        {row.config_fields.map((field) => (
          <div key={field} className="space-y-1 sm:w-48">
            <Label htmlFor={`cfg-${row.provider}-${field}`} className="text-xs">
              {CONFIG_FIELD_LABELS[field] ?? field}
            </Label>
            <Input
              id={`cfg-${row.provider}-${field}`}
              value={config[field] ?? ''}
              onChange={(e) => setConfig((prev) => ({ ...prev, [field]: e.target.value }))}
              disabled={busy}
            />
          </div>
        ))}
        <Button
          size="sm"
          onClick={() => connectMutation.mutate()}
          disabled={busy || apiKey.trim().length < 8}
        >
          {connectMutation.isPending ? (
            <Loader2 className="mr-1.5 h-3.5 w-3.5 animate-spin" />
          ) : (
            <Plug className="mr-1.5 h-3.5 w-3.5" />
          )}
          {row.connected ? 'Update' : 'Connect'}
        </Button>
      </div>
      {note && <p className="mt-2 text-xs text-amber-500">{note}</p>}
      {error && <p className="mt-2 text-xs text-destructive">{error}</p>}
    </div>
  )
}

function TestImageCard({ hasImageProvider }: { readonly hasImageProvider: boolean }) {
  const apiFetch = useApi()
  const [prompt, setPrompt] = useState('')
  const [result, setResult] = useState<{ url: string; provider: string } | null>(null)

  const generateMutation = useMutation({
    mutationFn: async (): Promise<{ url: string; provider: string }> => {
      const res = await apiFetch<{ image_url: string; provider: string }>(
        '/api/settings/integrations/generate-image',
        { method: 'POST', body: { prompt } },
      )
      if (!res.success || !res.data) throw new Error(res.error ?? 'Generation failed')
      return { url: res.data.image_url, provider: res.data.provider }
    },
    onSuccess: (data) => setResult(data),
  })

  const error = generateMutation.error instanceof Error ? generateMutation.error.message : null

  return (
    <div className="rounded-lg border border-dashed p-4">
      <div className="flex items-center gap-2">
        <Sparkles className="h-4 w-4 text-primary" />
        <span className="text-sm font-semibold">Try it: generate a test image</span>
      </div>
      <p className="mt-1 text-xs text-muted-foreground">
        Runs a real generation with your connected key. In the Content composer
        you can generate an image for any post and it publishes to LinkedIn
        with the post.
      </p>
      <div className="mt-3 flex gap-2">
        <Input
          placeholder="A minimal blue abstract background for a LinkedIn post"
          value={prompt}
          onChange={(e) => setPrompt(e.target.value)}
          disabled={!hasImageProvider || generateMutation.isPending}
        />
        <Button
          size="sm"
          onClick={() => generateMutation.mutate()}
          disabled={!hasImageProvider || prompt.trim().length < 3 || generateMutation.isPending}
        >
          {generateMutation.isPending ? (
            <Loader2 className="mr-1.5 h-3.5 w-3.5 animate-spin" />
          ) : (
            <ImageIcon className="mr-1.5 h-3.5 w-3.5" />
          )}
          Generate
        </Button>
      </div>
      {!hasImageProvider && (
        <p className="mt-2 text-xs text-muted-foreground">
          Connect fal.ai or Kie.ai above to enable generation.
        </p>
      )}
      {error && <p className="mt-2 text-xs text-destructive">{error}</p>}
      {result && (
        <div className="mt-3 space-y-1">
          <img
            src={result.url}
            alt="Generated test"
            className="max-h-64 rounded-lg border object-contain"
          />
          <p className="text-[11px] text-muted-foreground">
            Generated with {result.provider === 'kie' ? 'Kie.ai' : 'fal.ai'} using your key.
          </p>
        </div>
      )}
    </div>
  )
}

export default function IntegrationsSettings() {
  const apiFetch = useApi()
  const plan = usePlan()

  const { data, isLoading } = useQuery({
    queryKey: integrationKeys.list,
    queryFn: async (): Promise<IntegrationRow[]> => {
      const res = await apiFetch<IntegrationRow[]>('/api/settings/integrations')
      if (!res.success || !res.data) throw new Error(res.error ?? 'Failed to load integrations')
      return res.data
    },
    staleTime: 60_000,
  })

  const rows = data ?? []
  const hasImageProvider = rows.some((r) => r.category === 'image' && r.connected)
  const categories: IntegrationRow['category'][] = ['image', 'cold_email', 'crm']

  return (
    <PlanGate requiredPlan="pro" currentPlan={plan} replace>
      <div className="space-y-6">
        {isLoading && (
          <div className="flex items-center gap-2 text-sm text-muted-foreground">
            <Loader2 className="h-4 w-4 animate-spin" />
            Loading integrations
          </div>
        )}
        {categories.map((category) => {
          const meta = CATEGORY_META[category]
          const catRows = rows.filter((r) => r.category === category)
          if (catRows.length === 0 && !isLoading) return null
          return (
            <Card key={category}>
              <CardHeader>
                <div className="flex items-center gap-2">
                  <meta.Icon className="h-5 w-5 text-primary" />
                  <CardTitle>{meta.title}</CardTitle>
                </div>
                <CardDescription>{meta.desc}</CardDescription>
              </CardHeader>
              <CardContent className="space-y-3">
                {catRows.map((row) => (
                  <ProviderCard key={row.provider} row={row} />
                ))}
                {category === 'image' && <TestImageCard hasImageProvider={hasImageProvider} />}
              </CardContent>
            </Card>
          )
        })}
        <p className="text-xs text-muted-foreground">
          Your keys are encrypted before they are stored and are only used to
          run the actions you trigger. Disconnect at any time.
        </p>
      </div>
    </PlanGate>
  )
}
